import {
  BadgeCell,
  HtmlCell,
  ImageCell,
  ImageTextCell,
  TextCell,
} from "@/components/table/CustomCell";
import { Instance } from "@/context/auth/AuthContext";
import i18n, { getMainLanguage, SupportedLocale } from "@/i18n";
import {
  hasValidDimensions,
  isPhotoModuleEnabled,
} from "@/lib/compute-field/artwork-module-helpers";
import {
  computeFieldCollectorName,
  computeFieldDate,
  computeFieldDescriptionEnsembleFixeArtwork,
  computeFieldDescriptionEnsembleTemporaireArtwork,
  computeFieldDimensionsArtwork,
  computeFieldDimensionsCadreArtwork,
  computeFieldDimensionsHorsMargesArtwork,
  computeFieldEstimationFromMonetaryTransactionsArtwork,
  computeFieldRelatedAuthorsArtwork,
  computeFieldUnstyledArtwork,
} from "@/lib/computeFields";
import {
  Artwork,
  ArtworkInAuction,
  ArtworkInBibliography,
  ArtworkInExhibition,
} from "@/lib/types";
import { decodeHtmlEntities, parseJsonOrUndefined } from "@/lib/utils";
import { ColumnDef } from "@tanstack/react-table";
import { AdvancedSearchConfig, attachmentPhotoAdvancedSearchGroup } from "../forms/advanced-search/advanced-search-fields-def";
import { ARTIST_LAST_NAME_SORT_LABEL } from "../forms/advanced-search/artist-sort-fields";
import { ColumnFormatted } from "./general";
import {
  sculptureEditionProvenanceAdvancedSearchFields,
  withMasterArtworkSculptureEditionSearchKeys,
} from "./sculpture-editions";

export const artworkColumnsFormatted: ColumnFormatted[] = [
  {
    type: "string",
    key: "oeuvresDescriptionNumeroEnregistrement",
    visible: true,
  },
  {
    type: "image",
    key: "oeuvresDescriptionVignette",
    visible: true,
  },
  {
    type: "computed",
    key: "oeuvresDescriptionEnsemble",
    visible: false,
    formattedData(data) {
      return computeFieldDescriptionEnsembleFixeArtwork(data, {
        language: i18n.language as SupportedLocale,
      });
    },
  },
  {
    type: "computed",
    key: "oeuvresValidationAjoutUnEnsembleTemporaire",
    visible: false,
    formattedData(data) {
      return computeFieldDescriptionEnsembleTemporaireArtwork(data, {
        language: i18n.language as SupportedLocale,
      });
    },
  },
  {
    type: "computed",
    key: "oeuvresDescriptionNomArtiste",
    visible: true,
    formattedData(data) {
      return computeFieldRelatedAuthorsArtwork(data, {
        language: i18n.language as SupportedLocale,
      });
    },
  },
  {
    type: "computed",
    key: "oeuvresDescriptionTitreMultilingual",
    visible: true,
    formattedData(data) {
      const raw = computeFieldUnstyledArtwork(data, {
        language: i18n.language as SupportedLocale,
      });
      return decodeHtmlEntities(raw);
    },
  },
  {
    type: "boolean",
    key: "oeuvresDescriptionTitreSansTitre",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresDescriptionTitreInconnu",
    visible: false,
  },
  {
    type: "richtext",
    key: "oeuvresDescriptionDescriptionLongue",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionSousTitre",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionAutreTitre",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionReference",
    visible: false,
  },
  {
    type: "computed",
    key: "oeuvresDescriptionDateDeCreation",
    visible: true,
    formattedData(data) {
      return computeFieldDate(
        {
          date: data.oeuvresDescriptionDateDeCreation,
          unknownDate: data.oeuvresDescriptionDateDeCreationInconnue,
          uncertainDate: data.oeuvresDescriptionDateDeCreationIncertaine,
        },
        {
          language: i18n.language as SupportedLocale,
        },
      );
    },
  },
  {
    type: "boolean",
    key: "oeuvresDescriptionDateDeCreationInconnue",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresDescriptionDateDeCreationIncertaine",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresDescriptionDateDeCreationEstimee",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionPeriodeCreation",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionAireGeographique",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionEcoleMouvement",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionCategorie",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionSerie",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionTheme",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionLieuDeCreation",
    visible: false,
  },
  {
    type: "computed",
    key: "oeuvresDescriptionDimensionsOeuvre",
    visible: true,
    formattedData(data: Artwork, instanceData?: Instance) {
      if (!instanceData) {
        return computeFieldDimensionsArtwork(
          {
            oeuvresDescriptionDimensionsOeuvreRaw:
              data.oeuvresDescriptionDimensionsOeuvreRaw,
            oeuvresDescriptionDimensionsOeuvreInconnues:
              data.oeuvresDescriptionDimensionsOeuvreInconnues,
            oeuvresDescriptionDimensionsOeuvre: parseJsonOrUndefined(
              data.oeuvresDescriptionDimensionsOeuvre,
            ),
          },
          {
            language: i18n.language as SupportedLocale,
          },
        );
      }

      const photoModuleEnabled = isPhotoModuleEnabled(
        data,
        instanceData,
        i18n.language as SupportedLocale,
      );

      // Cacher le champ si module photo activé et pas de dimensions valides
      if (photoModuleEnabled && !hasValidDimensions(data)) {
        return "";
      }

      return computeFieldDimensionsArtwork(
        {
          oeuvresDescriptionDimensionsOeuvreRaw:
            data.oeuvresDescriptionDimensionsOeuvreRaw,
          oeuvresDescriptionDimensionsOeuvreInconnues:
            data.oeuvresDescriptionDimensionsOeuvreInconnues,
          oeuvresDescriptionDimensionsOeuvre: parseJsonOrUndefined(
            data.oeuvresDescriptionDimensionsOeuvre,
          ),
        },
        {
          language: i18n.language as SupportedLocale,
        },
      );
    },
  },
  {
    type: "boolean",
    key: "oeuvresDescriptionDimensionsOeuvreVerifiees",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresDescriptionDimensionsOeuvreInconnues",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresDescriptionDimensionsOeuvreDouteuses",
    visible: false,
  },
  {
    type: "computed",
    key: "oeuvresDescriptionDimensionsHorsMarges",
    visible: false,
    formattedData(data) {
      return computeFieldDimensionsHorsMargesArtwork(data, {
        language: i18n.language as SupportedLocale,
      });
    },
  },
  {
    type: "boolean",
    key: "oeuvresDescriptionDimensionsHorsMargesVerifiees",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresDescriptionDimensionsHorsMargesInconnues",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresDescriptionDimensionsHorsMargesDouteuses",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionPoidsOeuvre",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresDescriptionPoidsOeuvrePublished",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresDescriptionOeuvreDoubleFace",
    visible: false,
  },
  {
    type: "badge",
    key: "oeuvresDescriptionTypeOeuvreMultilingual",
    visible: true,
    formattedData(data) {
      if (
        Object.keys(data.oeuvresDescriptionTypeOeuvreMultilingual).length === 0
      )
        return "";
      return (
        data.oeuvresDescriptionTypeOeuvreMultilingual?.[
          i18n.language as SupportedLocale
        ] ?? ""
      );
    },
  },
  {
    type: "multilingual",
    key: "oeuvresDescriptionTechnique",
    visible: false,
  },
  {
    type: "multilingual",
    key: "oeuvresDescriptionRemarqueSupport",
    visible: false,
  },
  {
    type: "multilingual",
    key: "oeuvresDescriptionSupport",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionRealisationFabrication",
    visible: false,
  },
  {
    type: "multilingual",
    key: "oeuvresDescriptionSignatureLocalisation",
    visible: false,
  },
  {
    type: "multilingual",
    key: "oeuvresDescriptionSignatureTranscription",
    visible: false,
  },
  {
    type: "multilingual",
    key: "oeuvresDescriptionInscriptionsDosMultilingual",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionLegendeDocumentAnnexe",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionNomFichierNumerique",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionNumeroDInventaire",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionReferenceCollectionActuelle",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionNumeroAutre",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresInformationAutreNumeroPublished",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresInformationAncienNumero",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresInformationAncienNumeroPublished",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionAjoutDeMotsCles",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresValidationSystemeDePublicationDeLaFicheOnlineWebFront",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresValidationDescriptionOk",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresValidationHistoriqueCollectionsOk",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresValidationExpositionsOk",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresValidationBibliographieOk",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresValidationVentesOk",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresValidationSelectionnerFiche",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresValidationSource",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresValidationOeuvreDocumentaire",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresValidationOeuvreImportante",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresValidationOeuvreMajeure",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresValidationOeuvreInachevee",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresValidationAttributionDouteuse",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresValidationFaux",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresValidationCopie",
    visible: false,
  },
  {
    type: "richtext",
    key: "oeuvresValidationNotesDeTravail",
    visible: false,
  },
  {
    type: "multilingual",
    key: "oeuvresValidationRemarquesPubliquesMultilingual",
    visible: false,
  },
  {
    type: "richtext",
    key: "oeuvresValidationRemarquesPersonnelles",
    visible: false,
  },
  {
    type: "computed",
    key: "oeuvresLocalisationHistoriqueCollection",
    visible: false,
    formattedData(data: any) {
      return data.oeuvresLocalisationHistoriqueCollection
        ? computeFieldCollectorName(
            data.oeuvresLocalisationHistoriqueCollection,
            {
              language: i18n.language as SupportedLocale,
            },
          )
        : "";
    },
  },
  {
    type: "string",
    key: "oeuvresLocalisationHistoriqueCollectionCatalogue",
    visible: false,
  },
  {
    type: "richtext",
    key: "oeuvresLocalisationHistoriqueCollectionNote",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresLocalisationHistoriqueOeuvreNonLocalisee",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresLocalisationHistoriqueHistoriqueNom",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresLocalisationHistoriqueHistoriqueVille",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresLocalisationHistoriqueHistoriqueDates",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresLocalisationHistoriqueHistoriqueCollectionParticuliere",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresLocalisationHistoriqueLieuDeStockage",
    visible: false,
  },
  {
    type: "richtext",
    key: "oeuvresLocalisationHistoriqueNotesRangement",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresLocalisationHistoriqueMarquage",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresLocalisationHistoriqueDepotAffectionPret",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresLocalisationHistoriqueNoteDepotAffectionPret",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresLocalisationHistoriqueDateDepotAffectionPret",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresLocalisationHistoriqueDateRetourDepotAffectionPret",
    visible: false,
  },
  {
    type: "richtext",
    key: "oeuvresLocalisationHistoriqueNotes",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresRegieCadre",
    visible: false,
  },
  {
    type: "richtext",
    key: "oeuvresRegieDescriptionCadre",
    visible: false,
  },
  {
    type: "computed",
    key: "oeuvresRegieDimensionsCadre",
    visible: false,
    formattedData(data: Artwork) {
      return computeFieldDimensionsCadreArtwork(data, {
        language: i18n.language as SupportedLocale,
      });
    },
  },
  {
    type: "boolean",
    key: "oeuvresRegieCaisseTransport",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresRegieDescriptionCaisse",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresRegieDimensionsCaisse",
    visible: false,
  },

  {
    type: "string",
    key: "oeuvresRegiePoidsCaisse",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresRegiePoidsCaisseAvecOeuvre",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresRegieNotesRegie",
    visible: false,
  },
  {
    type: "richtext",
    key: "oeuvresRegieNotesCondition",
    visible: false,
  },
  {
    type: "richtext",
    key: "oeuvresPhotosNotesPhotos",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresPhotosPhotoScanAFaire",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresPhotosPhotoOkWeb",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresPhotosPhotoHdOk",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresPhotosNotesPreparationPhotos",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresCreditsCreditOeuvre",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresCreditsCreditPhotoOeuvre",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresCreditsNatureDroits",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresCreditsMontantDroits",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresCreditsRemarqueCreditsDroits",
    visible: false,
  },
  {
    type: "computed",
    key: "oeuvresValorisationEstimation",
    visible: false,
    formattedData(data) {
      return computeFieldEstimationFromMonetaryTransactionsArtwork(data, {
        language: i18n.language as SupportedLocale,
      });
    },
  },
  {
    type: "string",
    key: "oeuvresValorisationValeurAssurance",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresValorisationDateValeurAssurance",
    visible: false,
  },
  {
    type: "richtext",
    key: "oeuvresValorisationRemarqueValeurAssurance",
    visible: false,
  },
  {
    type: "multilingual",
    key: "oeuvresSocleDescription",
    visible: false,
  },
  {
    type: "badge",
    key: "oeuvresSocleDescriptionPublished",
    visible: false,
  },
  {
    type: "dimensions",
    key: "oeuvresSocleDimensions",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresSoclePoids",
    visible: false,
  },
  {
    type: "multilingual",
    key: "oeuvresSculptureDescriptionEditions",
    visible: false,
  },
  {
    type: "badge",
    key: "oeuvresSculptureDescriptionEditionsPublished",
    visible: false,
  },
  {
    type: "multilingual",
    key: "oeuvresSculpturePresentationSculpture",
    visible: false,
  },
  {
    type: "badge",
    key: "oeuvresSculpturePresentationSculpturePublished",
    visible: false,
  },
  {
    type: "multilingual",
    key: "oeuvresSculptureNotesGenerales",
    visible: false,
  },
  {
    type: "badge",
    key: "oeuvresSculptureNotesGeneralesPublished",
    visible: false,
  },
  {
    type: "multilingual",
    key: "oeuvresSculptureMouleDescription",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresSculptureMouleNombreElements",
    visible: false,
  },
  {
    type: "dimensions",
    key: "oeuvresSculptureMouleDimensions",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresSculptureMouleDimensionsPersonnalisees",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresSculptureMoulePoids",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresSculptureMouleLocalisation",
    visible: false,
  },
  {
    type: "badge",
    key: "oeuvresSculptureMouleNonLocalise",
    visible: false,
  },
  {
    type: "badge",
    key: "oeuvresSculptureMouleDestroy",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresSculptureMouleFabricant",
    visible: false,
  },
  {
    type: "currency",
    key: "oeuvresSculptureMouleCoutRevient",
    visible: false,
  },
  {
    type: "multilingual",
    key: "oeuvresSculptureMouleNotes",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresStatus",
    visible: false,
  },
  {
    type: "status",
    key: "oeuvresPublicationStatus",
    visible: true,
  },
];

// Currently unused
export const artworkColumns = [
  "oeuvresDescriptionNumeroEnregistrement",
  "oeuvresDescriptionVignette",
  "oeuvresDescriptionTitre",
  "oeuvresDescriptionNomArtiste",
  "oeuvresDescriptionDimensionsOeuvre",
  "oeuvresDescriptionDimensionsHorsMarges",
  "oeuvresDescriptionTypeOeuvre",
  "oeuvresPublicationStatus",
  "sources",
  "oeuvresDescriptionNomArtisteAttribueA",
  "oeuvresDescriptionNomArtisteAnonyme",
  "oeuvresDescriptionNomArtisteDansLeGoutDe",
  "oeuvresDescriptionNomArtisteEntourageDe",
  "oeuvresDescriptionNomArtisteDApres",
  "oeuvresDescriptionNomArtisteAtelierDe",
  "oeuvresDescriptionTitreSansTitre",
  "oeuvresDescriptionTitreInconnu",
  "oeuvresDescriptionDescriptionLongue",
  "oeuvresDescriptionSousTitre",
  "oeuvresDescriptionAutreTitre",
  "oeuvresDescriptionReference",
  "oeuvresDescriptionDateDeCreation",
  "oeuvresDescriptionDateDeCreationInconnue",
  "oeuvresDescriptionDateDeCreationIncertaine",
  "oeuvresDescriptionDateDeCreationEstimee",
  "oeuvresDescriptionPeriodeCreation",
  "oeuvresDescriptionAireGeographique",
  "oeuvresDescriptionEcoleMouvement",
  "oeuvresDescriptionCategorie",
  "oeuvresDescriptionSerie",
  "oeuvresDescriptionTheme",
  "oeuvresDescriptionLieuDeCreation",
  "oeuvresDescriptionDimensionsOeuvreVerifiees",
  "oeuvresdescriptiondimensionsprofondeur",
  "oeuvresDescriptionDimensionsOeuvreInconnues",
  "oeuvresDescriptionDimensionsOeuvreDouteuses",
  "oeuvresDescriptionDimensionsHorsMargesVerifiees",
  "oeuvresDescriptionDimensionsHorsMargesInconnues",
  "oeuvresDescriptionDimensionsHorsMargesDouteuses",
  "oeuvresDescriptionPoidsOeuvre",
  "oeuvresDescriptionOeuvreDoubleFace",
  "oeuvresDescriptionTechnique",
  "oeuvresDescriptionSupport",
  "oeuvresDescriptionRemarqueSupport",
  "oeuvresDescriptionRealisationFabrication",
  "oeuvresDescriptionSignatureLocalisation",
  "oeuvresDescriptionSignatureTranscription",
  "oeuvresDescriptionInscriptionsDosMultilingual",
  "oeuvresDescriptionResume",
  "oeuvresDescriptionLegendeDocumentAnnexe",
  "oeuvresDescriptionNomFichierNumerique",
  "oeuvresDescriptionNumeroDInventaire",
  "oeuvresDescriptionReferenceCollectionActuelle",
  "oeuvresDescriptionNumeroAutre",
  "oeuvresDescriptionChampParametrableUtilisateur",
  "oeuvresDescriptionAjoutDeMotsCles",
  "oeuvresValidationSystemeDeNotationDeLoeuvre",
  "oeuvresValidationSystemeDePublicationDeLaFicheOnlineWebFront",
  "oeuvresValidationDescriptionOk",
  "oeuvresValidationHistoriqueCollectionsOk",
  "oeuvresValidationExpositionsOk",
  "oeuvresValidationBibliographieOk",
  "oeuvresValidationVentesOk",
  "oeuvresValidationSelectionnerFiche",
  "oeuvresValidationSource",
  "oeuvresValidationOeuvreDocumentaire",
  "oeuvresValidationOeuvreImportante",
  "oeuvresValidationOeuvreMajeure",
  "oeuvresValidationOeuvreInachevee",
  "oeuvresValidationAttributionDouteuse",
  "oeuvresValidationFaux",
  "oeuvresValidationCopie",
  "oeuvresValidationLost",
  "oeuvresValidationTitleNotConfirmed",
  "oeuvresValidationDestroy",
  "oeuvresDescriptionRestaurationNumerique",
  "oeuvresValidationStole",
  "oeuvresValidationNotesDeTravail",
  "oeuvresValidationRemarquesPubliquesMultilingual",
  "oeuvresValidationRemarquesPersonnelles",
  "oeuvresLocalisationHistoriqueCollectionCatalogue",
  "oeuvresLocalisationHistoriqueCollectionNote",
  "oeuvresLocalisationHistoriqueOeuvreNonLocalisee",
  "oeuvresLocalisationHistoriqueHistoriqueNom",
  "oeuvresLocalisationHistoriqueHistoriqueVille",
  "oeuvresLocalisationHistoriqueHistoriqueDates",
  "oeuvresLocalisationHistoriqueHistoriqueCollectionParticuliere",
  "oeuvresLocalisationHistoriqueLieuDeStockage",
  "oeuvresLocalisationHistoriqueNotesRangement",
  "oeuvresLocalisationHistoriqueMarquage",
  "oeuvresLocalisationHistoriqueDepotAffectionPret",
  "oeuvresLocalisationHistoriqueNoteDepotAffectionPret",
  "oeuvresLocalisationHistoriqueDateDepotAffectionPret",
  "oeuvresLocalisationHistoriqueDateRetourDepotAffectionPret",
  "oeuvresLocalisationHistoriqueNotes",
  "oeuvresRegieCadre",
  "oeuvresRegieDescriptionCadre",
  "oeuvresRegieDimensionsCadre",
  "oeuvresRegieCaisseTransport",
  "oeuvresRegieDescriptionCaisse",
  "oeuvresRegieDimensionsCaisse",
  "oeuvresRegiePoidsCaisse",
  "oeuvresRegiePoidsCaisseAvecOeuvre",
  "oeuvresRegiePhotoCaisse",
  "oeuvresRegieNotesRegie",
  "oeuvresRegieNotesCondition",
  "oeuvresPhotosNotesPhotos",
  "oeuvresPhotosPhotoScanAFaire",
  "oeuvresPhotosPhotoOkWeb",
  "oeuvresPhotosPhotoHdOk",
  "oeuvresPhotosNotesPreparationPhotos",
  "oeuvresCreditsCreditOeuvre",
  "oeuvresCreditsCreditPhotoOeuvre",
  "oeuvresCreditsNatureDroits",
  "oeuvresCreditsMontantDroits",
  "oeuvresCreditsRemarqueCreditsDroits",
  "oeuvresValorisationEstimation",
  "oeuvresValorisationValeurAssurance",
  "oeuvresValorisationDateValeurAssurance",
  "oeuvresValorisationRemarqueValeurAssurance",
  "oeuvresDocumentsAdministratifsNotesDocumentsAdministratifs",
  "oeuvresDocumentsAdministratifsDocumentEdite",
  "oeuvresDocumentsAdministratifsLieuEtDateDocument",
  "oeuvresDescriptionTechniqueConfirmer",
  "oeuvresDescriptionSupportConfirmer",
  "oeuvresDescriptionNewBdOk",
  "oeuvresRangementRecolementMarquage",
  "oeuvresRangementRecolementRack",
  "oeuvresRangementRecolementPieceBox",
  "oeuvresRangementRecolementRangee",
  "oeuvresRangementRecolementMeuble",
  "oeuvresRangementRecolementEtagere",
  "oeuvresRangementRecolementBoiteCarton",
  "oeuvresRangementRecolementClasseur",
  "oeuvresRangementRecolementAutreReferenceRangement",
  "oeuvresRangementRecolementDateRangement",
  "oeuvresRangementRecolementDateMarquage",
  "oeuvresRangementRecolementOperateurRecolement",
  "oeuvresRangementRecolementDateRecolement",
  "oeuvresRangementRecolementReferenceCampagneRecolement",
  "oeuvresRangementRecolementAutreReferenceRecolement",
  "oeuvresRangementRecolementRecolementOk",
  "oeuvresAdminDocLettreInclusionEdit",
  "oeuvresAdminDocCertificatExist",
  "oeuvresRangementRecolementNotesRecolement",
  "oeuvresAdministratifNotes",
];

export const artworkColumnsVisibility = artworkColumnsFormatted.reduce(
  (acc, column) => ({
    ...acc,
    [column.key]: column.visible || false,
  }),
  {},
);

/*
 *
 * This is used to compute columns in CSV File for export
 *
 */
export const computedColumns: {
  [key: string]: (data: Artwork) => string;
} = {
  oeuvresDescriptionDimensionsOeuvre: (data) =>
    computeFieldDimensionsArtwork(data, {
      language: i18n.language as SupportedLocale,
    }),
  oeuvresRegieDimensionsCadre: (data) =>
    computeFieldDimensionsCadreArtwork(data, {
      language: i18n.language as SupportedLocale,
    }),
  oeuvresDescriptionDimensionsHorsMarges: (data) =>
    computeFieldDimensionsHorsMargesArtwork(data, {
      language: i18n.language as SupportedLocale,
    }),
  oeuvresDescriptionNomArtiste: (data) =>
    computeFieldRelatedAuthorsArtwork(data, {
      language: i18n.language as SupportedLocale,
    }),
};

// Currently unused
export const getArtworkColumns = (): ColumnDef<Artwork, any>[] => {
  return [
    {
      accessorKey: "oeuvresDescriptionNumeroEnregistrement",
      header: "N°",
      enableResizing: true,
      cell: (props) => {
        return <TextCell value={props.getValue()} />;
      },
    },
    {
      accessorKey: "oeuvresDescriptionVignette",
      header: "",
      size: 50,
      cell: (props) => {
        return (
          <ImageCell
            value={props.getValue()}
            attachments={props.row.original.attachments ?? []}
            domain="artwork"
          />
        );
      },
    },
    {
      accessorKey: "oeuvresDescriptionTitre",
      header: "Titre",
      size: 200,
      cell: (props) => {
        return <TextCell value={props.getValue()} />;
      },
    },
    {
      accessorKey: "oeuvresDescriptionAutreTitre",
      header: "Autre titre",
      cell: (props) => {
        return <TextCell value={props.getValue()} />;
      },
    },
    {
      accessorKey: "oeuvresDescriptionNomArtiste",
      header: "Artiste",
      cell: (props) => {
        return (
          <TextCell
            value={computeFieldRelatedAuthorsArtwork(props.row.original, {
              language: i18n.language as SupportedLocale,
            })}
          />
        );
      },
    },
    {
      accessorKey: "oeuvresDescriptionTypeOeuvreMultilingual",
      header: "Type d'oeuvre",
      cell: (props) => {
        if (
          Object.keys(
            props.row.original.oeuvresDescriptionTypeOeuvreMultilingual,
          ).length === 0
        )
          return null;
        return (
          <BadgeCell
            value={
              props.row.original.oeuvresDescriptionTypeOeuvreMultilingual[
                i18n.language as SupportedLocale
              ] ?? ""
            }
            variant="outlined"
          />
        );
      },
    },
    {
      accessorKey: "oeuvresDescriptionDescriptionLongue",
      header: "Description longue",
      cell: (props) => {
        return <TextCell value={props.getValue()} />;
      },
    },
    {
      accessorKey: "oeuvresDescriptionSousTitre",
      header: "Sous titre",
      cell: (props) => {
        return <TextCell value={props.getValue()} />;
      },
    },
    {
      accessorKey: "oeuvresDescriptionReference",
      header: "Référence",
      cell: (props) => {
        return <TextCell value={props.getValue()} />;
      },
    },
    {
      accessorKey: "oeuvresDescriptionDimensionsHorsMarges",
      header: "Dimensions hors marges",
      cell: (props) => {
        return (
          props.row.original && (
            <HtmlCell
              value={computeFieldDimensionsArtwork(props.row.original, {
                language: i18n.language as SupportedLocale,
              })}
            />
          )
        );
      },
    },
    {
      accessorKey: "oeuvresDescriptionDimensionsOeuvre",
      header: "Dimensions",
      cell: (props) => {
        return (
          props.row.original && (
            <HtmlCell
              value={computeFieldDimensionsArtwork(props.row.original, {
                language: i18n.language as SupportedLocale,
              })}
            />
          )
        );
      },
    },
    {
      accessorKey: "oeuvresPublicationStatus",
      header: "Statut",
      size: 100,
      cell: (props) => {
        return (
          <BadgeCell
            value={props.getValue() === "published" ? "Publié" : "Brouillon"}
            variant={
              props.row.original.oeuvresPublicationStatus === "published"
                ? "published"
                : "draft"
            }
          />
        );
      },
    },
    {
      accessorKey: "oeuvresDescriptionDateDeCreation",
      header: "Date de création",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresDescriptionPeriodeCreation",
      header: "Période de création",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresDescriptionAireGeographique",
      header: "Aire géographique",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresDescriptionEcoleMouvement",
      header: "École/Mouvement",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresDescriptionCategorie",
      header: "Catégorie",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresDescriptionSerie",
      header: "Série",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresDescriptionTheme",
      header: "Thème",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresDescriptionLieuDeCreation",
      header: "Lieu de création",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresDescriptionTechnique",
      header: "Technique",
      cell: (props) => (
        <TextCell
          value={
            props.row.original.oeuvresDescriptionTechnique[
              getMainLanguage() as SupportedLocale
            ] ?? ""
          }
        />
      ),
    },
    {
      accessorKey: "oeuvresDescriptionSupport",
      header: "Support",
      cell: (props) => (
        <TextCell
          value={
            props.row.original.oeuvresDescriptionSupport[
              getMainLanguage() as SupportedLocale
            ] ?? ""
          }
        />
      ),
    },
    {
      accessorKey: "oeuvresDescriptionPoidsOeuvre",
      header: "Poids",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresDescriptionSignatureLocalisation",
      header: "Localisation signature",
      cell: (props) => (
        <TextCell
          value={
            props.row.original.oeuvresDescriptionSignatureLocalisation[
              getMainLanguage() as SupportedLocale
            ] ?? ""
          }
        />
      ),
    },
    {
      accessorKey: "oeuvresDescriptionSignatureTranscription",
      header: "Transcription signature",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresDescriptionInscriptionsDosMultilingual",
      header: "Inscriptions dos",
      cell: (props) => (
        <TextCell
          value={
            props.row.original.oeuvresDescriptionInscriptionsDosMultilingual[
              getMainLanguage() as SupportedLocale
            ] ?? ""
          }
        />
      ),
    },
    {
      accessorKey: "oeuvresDescriptionResume",
      header: "Résumé",
      size: 200,
      cell: (props) => <HtmlCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresDescriptionNumeroDInventaire",
      header: "N° Inventaire",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresValidationSystemeDeNotationDeLoeuvre",
      header: "Notation",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresValidationRemarquesPubliquesMultilingual",
      header: "Remarques publiques",
      cell: (props) => (
        <TextCell
          value={
            props.row.original.oeuvresValidationRemarquesPubliquesMultilingual?.[
              getMainLanguage() as SupportedLocale
            ] ?? ""
          }
        />
      ),
    },
    {
      accessorKey: "oeuvresLocalisationHistoriqueLieuDeStockage",
      header: "Lieu de stockage",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresValidationDescriptionOk",
      header: "Description validée",
      cell: (props) => (
        <BadgeCell
          value={props.getValue() ? "Oui" : "Non"}
          variant={props.getValue() ? "published" : "draft"}
        />
      ),
    },
    {
      accessorKey: "oeuvresValidationHistoriqueCollectionsOk",
      header: "Historique validé",
      cell: (props) => (
        <BadgeCell
          value={props.getValue() ? "Oui" : "Non"}
          variant={props.getValue() ? "published" : "draft"}
        />
      ),
    },
    {
      accessorKey: "oeuvresValidationExpositionsOk",
      header: "Expositions validées",
      cell: (props) => (
        <BadgeCell
          value={props.getValue() ? "Oui" : "Non"}
          variant={props.getValue() ? "published" : "draft"}
        />
      ),
    },
    {
      accessorKey: "oeuvresValidationBibliographieOk",
      header: "Bibliographie validée",
      cell: (props) => (
        <BadgeCell
          value={props.getValue() ? "Oui" : "Non"}
          variant={props.getValue() ? "published" : "draft"}
        />
      ),
    },
    {
      accessorKey: "oeuvresValidationVentesOk",
      header: "Ventes validées",
      cell: (props) => (
        <BadgeCell
          value={props.getValue() ? "Oui" : "Non"}
          variant={props.getValue() ? "published" : "draft"}
        />
      ),
    },
    {
      accessorKey: "oeuvresValidationOeuvreDocumentaire",
      header: "Œuvre documentaire",
      cell: (props) => (
        <BadgeCell
          value={props.getValue() ? "Oui" : "Non"}
          variant={props.getValue() ? "published" : "draft"}
        />
      ),
    },
    {
      accessorKey: "oeuvresValidationOeuvreImportante",
      header: "Œuvre importante",
      cell: (props) => (
        <BadgeCell
          value={props.getValue() ? "Oui" : "Non"}
          variant={props.getValue() ? "published" : "draft"}
        />
      ),
    },
    {
      accessorKey: "oeuvresValidationOeuvreMajeure",
      header: "Œuvre majeure",
      cell: (props) => (
        <BadgeCell
          value={props.getValue() ? "Oui" : "Non"}
          variant={props.getValue() ? "published" : "draft"}
        />
      ),
    },
    {
      accessorKey: "oeuvresValidationOeuvreInachevee",
      header: "Œuvre inachevée",
      cell: (props) => (
        <BadgeCell
          value={props.getValue() ? "Oui" : "Non"}
          variant={props.getValue() ? "published" : "draft"}
        />
      ),
    },
    {
      accessorKey: "oeuvresValidationAttributionDouteuse",
      header: "Attribution douteuse",
      cell: (props) => (
        <BadgeCell value={props.getValue() ? "Oui" : "Non"} variant="draft" />
      ),
    },
    {
      accessorKey: "oeuvresValidationFaux",
      header: "Faux",
      cell: (props) => (
        <BadgeCell value={props.getValue() ? "Oui" : "Non"} variant="draft" />
      ),
    },
    {
      accessorKey: "oeuvresValidationCopie",
      header: "Copie",
      cell: (props) => (
        <BadgeCell value={props.getValue() ? "Oui" : "Non"} variant="draft" />
      ),
    },
    {
      accessorKey: "oeuvresValidationLost",
      header: "Perdue",
      cell: (props) => (
        <BadgeCell value={props.getValue() ? "Oui" : "Non"} variant="draft" />
      ),
    },
    {
      accessorKey: "oeuvresValidationStole",
      header: "Volée",
      cell: (props) => (
        <BadgeCell value={props.getValue() ? "Oui" : "Non"} variant="draft" />
      ),
    },
    {
      accessorKey: "oeuvresValidationDestroy",
      header: "Détruite",
      cell: (props) => (
        <BadgeCell value={props.getValue() ? "Oui" : "Non"} variant="draft" />
      ),
    },
    {
      accessorKey: "oeuvresLocalisationHistoriqueHistoriqueNom",
      header: "Nom historique",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresLocalisationHistoriqueHistoriqueVille",
      header: "Ville historique",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresLocalisationHistoriqueHistoriqueDates",
      header: "Dates historiques",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresRegieCadre",
      header: "Cadre",
      cell: (props) => (
        <BadgeCell
          value={props.getValue() ? "Oui" : "Non"}
          variant={props.getValue() ? "published" : "draft"}
        />
      ),
    },
    {
      accessorKey: "oeuvresRegieDescriptionCadre",
      header: "Description cadre",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresRegieDimensionsCadre",
      header: "Dimensions cadre",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresCreditsCreditOeuvre",
      header: "Crédit œuvre",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresCreditsCreditPhotoOeuvre",
      header: "Crédit photo",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresCreditsNatureDroits",
      header: "Nature des droits",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresValorisationEstimation",
      header: "Estimation",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresValorisationValeurAssurance",
      header: "Valeur assurance",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresRangementRecolementRack",
      header: "Rack",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresRangementRecolementPieceBox",
      header: "Box",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresRangementRecolementMeuble",
      header: "Meuble",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresRangementRecolementEtagere",
      header: "Étagère",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
  ];
};

export const getArtworkForComplexLink = (): ColumnDef<
  ArtworkInBibliography | ArtworkInExhibition | ArtworkInAuction,
  any
>[] => {
  return [
    {
      accessorKey: "artwork.oeuvresDescriptionNumeroEnregistrement",
      header: "oeuvresDescriptionNumeroEnregistrement",
      enableResizing: true,
      cell: (props: any) => {
        return <TextCell value={props.getValue()} />;
      },
    },
    {
      accessorKey: "vignette",
      header: "",
      size: 100,
      cell: (props: any) => {
        const attachments =
          props.row.original.artwork?.attachments ??
          props.row.original.attachments ??
          [];
        return (
          <ImageCell value="" attachments={attachments} domain="artwork" />
        );
      },
    },
    {
      accessorKey: "artwork.oeuvresDescriptionTitre",
      header: "oeuvresDescriptionTitre",
      size: 200,
      cell: (props: any) => {
        const attachments =
          props.row.original.artwork?.attachments ??
          props.row.original.attachments ??
          [];
        return (
          <ImageTextCell
            value={props.getValue()}
            attachments={attachments}
            domain="artwork"
          />
        );
      },
    },
    {
      accessorKey: "artwork.oeuvresDescriptionDateDeCreation",
      header: "oeuvresDescriptionDateDeCreation",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "artwork.oeuvresDescriptionDimensionsOeuvre",
      header: "oeuvresDescriptionDimensionsOeuvre",
      cell: (props) => {
        return (
          props.row.original && (
            <HtmlCell
              value={computeFieldDimensionsArtwork(
                {
                  oeuvresDescriptionDimensionsOeuvreRaw:
                    props.row.original.artwork
                      .oeuvresDescriptionDimensionsOeuvreRaw,
                  oeuvresDescriptionDimensionsOeuvreInconnues:
                    props.row.original.artwork
                      .oeuvresDescriptionDimensionsOeuvreInconnues,
                  oeuvresDescriptionDimensionsOeuvre: parseJsonOrUndefined(
                    props.row.original.artwork
                      .oeuvresDescriptionDimensionsOeuvre,
                  ),
                },
                {
                  language: i18n.language as SupportedLocale,
                },
              )}
            />
          )
        );
      },
    },
    {
      accessorKey: "artwork.oeuvresDescriptionTypeOeuvreMultilingual",
      header: "oeuvresDescriptionTypeOeuvre",
      cell: (props: any) => {
        if (
          Object.keys(
            props.row.original?.artwork
              ?.oeuvresDescriptionTypeOeuvreMultilingual,
          ).length === 0
        )
          return null;
        return (
          <BadgeCell
            value={
              props.row.original?.artwork
                ?.oeuvresDescriptionTypeOeuvreMultilingual[
                i18n.language as SupportedLocale
              ] ?? ""
            }
            variant="outlined"
          />
        );
      },
    },
  ];
};

export const artworkAdvancedSearchConfig: AdvancedSearchConfig = [
  {
    type: "group",
    label: "artworksGeneralInfo",
    fields: [
      {
        type: "temporarySet",
        key: "relatedEnsemble",
        label: "advancedSearchTemporarySet",
      },
      { type: "string", key: "oeuvresDescriptionNumeroEnregistrement" },
      {
        type: "boolean",
        key: "oeuvresDescriptionNumeroEnregistrementPublished",
      },
      { type: "string", key: "oeuvresDescriptionReferenceCollectionActuelle" },
      { type: "string", key: "oeuvresDescriptionTitre" },
      { type: "boolean", key: "oeuvresPhotosPhotoScanAFaire" },
      { type: "boolean", key: "oeuvresPhotosPhotoOkWeb" },
      { type: "boolean", key: "oeuvresPhotosPhotoHdOk" },
      { type: "string", key: "oeuvresDescriptionResume" },
      { type: "string", key: "oeuvresDescriptionNumeroAutre" },
      { type: "number", key: "oeuvresValidationSystemeDeNotationDeLoeuvre" },
      { type: "boolean", key: "oeuvresValidationDescriptionOk" },
      { type: "boolean", key: "oeuvresValidationHistoriqueCollectionsOk" },
      { type: "boolean", key: "oeuvresValidationExpositionsOk" },
      { type: "boolean", key: "oeuvresValidationBibliographieOk" },
      { type: "boolean", key: "oeuvresValidationVentesOk" },
      { type: "boolean", key: "oeuvresValidationSelectionnerFiche" },
      { type: "string", key: "oeuvresValidationSource" },
      { type: "boolean", key: "oeuvresValidationOeuvreDocumentaire" },
      { type: "boolean", key: "oeuvresValidationOeuvreImportante" },
      { type: "boolean", key: "oeuvresWaitingPublication" },
      { type: "boolean", key: "oeuvresNotPublished" },
      { type: "boolean", key: "oeuvresValidationOeuvreMajeure" },
      { type: "boolean", key: "oeuvresValidationOeuvreInachevee" },
      { type: "boolean", key: "oeuvresValidationAttributionDouteuse" },
      { type: "boolean", key: "oeuvresValidationFaux" },
      { type: "boolean", key: "oeuvresValidationCopie" },
      { type: "boolean", key: "oeuvresValidationLost" },
      { type: "boolean", key: "oeuvresValidationDestroy" },
      { type: "boolean", key: "oeuvresDescriptionRestaurationNumerique" },
      { type: "boolean", key: "oeuvresValidationStole" },
      { type: "string", key: "oeuvresValidationNotesDeTravail" },
      { type: "string", key: "oeuvresDescriptionAjoutDeMotsCles" },
      { type: "string", key: "oeuvresDescriptionNumeroDInventaire" },
      { type: "boolean", key: "oeuvresDescriptionNumeroDInventairePublished" },
      { type: "string", key: "oeuvresInformationAncienNumero" },
      { type: "boolean", key: "oeuvresInformationAncienNumeroPublished" },
      { type: "boolean", key: "oeuvresInformationAutreNumeroPublished" },
      { type: "string", key: "oeuvresStatus" },
    ],
  },
  {
    type: "group",
    label: "artworksDescription",
    fields: [
      { type: "boolean", key: "oeuvresDescriptionNomArtisteAttribueA" },
      { type: "boolean", key: "oeuvresDescriptionNomArtisteAnonyme" },
      { type: "boolean", key: "oeuvresDescriptionNomArtisteDansLeGoutDe" },
      { type: "boolean", key: "oeuvresDescriptionNomArtisteEntourageDe" },
      { type: "boolean", key: "oeuvresDescriptionNomArtisteDApres" },
      { type: "boolean", key: "oeuvresDescriptionNomArtisteAtelierDe" },
      { type: "boolean", key: "oeuvresDescriptionTitreSansTitre" },
      { type: "boolean", key: "oeuvresDescriptionTitreInconnu" },
      { type: "boolean", key: "oeuvresValidationTitleNotConfirmed" },
      { type: "string", key: "oeuvresDescriptionDescriptionLongue" },
      { type: "string", key: "oeuvresDescriptionSousTitre" },
      { type: "string", key: "oeuvresDescriptionAutreTitre" },
      { type: "string", key: "oeuvresDescriptionReference" },
      {
        type: "string",
        key: "oeuvresPhotoProjectDescription",
        showWhen: (instanceData?: Instance) =>
          instanceData?.isModulePhotoEnabled ?? false,
      },
      { type: "string", key: "oeuvresPhotoPeopleAndModelNames" },
      { type: "string", key: "oeuvresDescriptionDateDeCreation" },
      { type: "boolean", key: "oeuvresDescriptionDateDeCreationInconnue" },
      { type: "boolean", key: "oeuvresDescriptionDateDeCreationIncertaine" },
      { type: "boolean", key: "oeuvresDescriptionDateDeCreationEstimee" },
      { type: "string", key: "oeuvresDescriptionPeriodeCreation" },
      { type: "string", key: "oeuvresDescriptionAireGeographique" },
      { type: "string", key: "oeuvresDescriptionEcoleMouvement" },
      { type: "string", key: "oeuvresDescriptionCategorie" },
      { type: "string", key: "oeuvresDescriptionSerie" },
      { type: "string", key: "oeuvresDescriptionTheme" },
      { type: "string", key: "oeuvresDescriptionLieuDeCreation" },
      { type: "string", key: "oeuvresDescriptionPoidsOeuvre" },
      { type: "boolean", key: "oeuvresDescriptionOeuvreDoubleFace" },
      { type: "multilingual", key: "oeuvresDescriptionTypeOeuvreMultilingual" },
      { type: "boolean", key: "oeuvresDescriptionTechniqueConfirmer" },
      { type: "boolean", key: "oeuvresDescriptionSupportConfirmer" },
      { type: "string", key: "oeuvresDescriptionRealisationFabrication" },
      { type: "string", key: "oeuvresDescriptionSignatureTranscription" },
      { type: "string", key: "oeuvresDescriptionNomFichierNumerique" },
      { type: "boolean", key: "oeuvresDescriptionRestaurationNumerique" },
      {
        type: "multilingual",
        key: "oeuvresDescriptionInscriptionsDosMultilingual",
      },
      { type: "multilingual", key: "oeuvresDescriptionRemarqueSupport" },
      { type: "multilingual", key: "oeuvresDescriptionSignatureLocalisation" },
      { type: "multilingual", key: "oeuvresDescriptionSupport" },
      { type: "multilingual", key: "oeuvresDescriptionTechnique" },
    ],
  },
  {
    type: "group",
    label: "artworksSculptureSocle",
    showWhen: (instanceData?: Instance) =>
      instanceData?.isModuleSculptureEnabled ?? false,
    fields: [
      {
        type: "multilingual",
        key: "oeuvresSocleDescription",
      },
      {
        type: "boolean",
        key: "oeuvresSocleDescriptionPublished",
      },
      {
        type: "dimensions",
        key: "oeuvresSocleDimensions",
        exportOnly: true,
      },
      {
        type: "string",
        key: "oeuvresSoclePoids",
      },
    ],
  },
  {
    type: "group",
    label: "artworkAuteurInfo",
    fields: [
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursDescriptionPrenom",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursDescriptionNom",
        sortable: true,
        sortLabel: ARTIST_LAST_NAME_SORT_LABEL,
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursDescriptionEtatCivil",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursDescriptionDateNaissance",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursDescriptionVilleDeNaissance",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursDescriptionDateDeDeces",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursDescriptionVilleDeDeces",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursDescriptionNationalite",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursDescriptionResumeDates",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursDescriptionPaysDActivite",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursDescriptionActivite",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursDescriptionPeriodeDactivit",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursDescriptionNotesDeTravail",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursDescriptionNotesBiographiques",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursAyantDroitsNom",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursAyantDroitsContact",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursAyantDroitsLienAvecLAuteur",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursAyantDroitsOrganismeGerantLesDroits",
      },
      { type: "string", key: "relatedAuthor.some.artistesAuteursExpertCrNom" },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursExpertCrContact",
      },
      {
        type: "boolean",
        key: "relatedAuthor.some.artistesAuteursExpertCrCatalogueRaisonne",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursExpertCrEtatDuCatalogueRaisonne",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursExpertCrDescriptionDuCatalogueRaisonne",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursPublicationStatus",
      },
      { type: "string", key: "relatedAuthor.some.sources" },
    ],
  },
  {
    type: "group",
    label: "artworksRangementRecolement",
    fields: [
      {
        type: "string",
        key: "oeuvresLocalisationHistoriqueLieuDeStockage",
      },
      {
        type: "string",
        key: "oeuvresRangementRecolementPieceBox",
      },
      {
        type: "string",
        key: "oeuvresRangementRecolementRangee",
      },
      {
        type: "string",
        key: "oeuvresRangementRecolementRack",
      },
      {
        type: "string",
        key: "oeuvresRangementRecolementMeuble",
      },
      {
        type: "string",
        key: "oeuvresRangementRecolementEtagere",
      },
      {
        type: "string",
        key: "oeuvresRangementRecolementBoiteCarton",
      },
      {
        type: "string",
        key: "oeuvresRangementRecolementClasseur",
      },
      {
        type: "string",
        key: "oeuvresRangementRecolementAutreReferenceRangement",
      },
      {
        type: "string",
        key: "oeuvresRangementRecolementDateRangement",
      },
      {
        type: "string",
        key: "oeuvresLocalisationHistoriqueNotesRangement",
      },
      {
        type: "boolean",
        key: "oeuvresRangementRecolementMarquage",
      },
      {
        type: "string",
        key: "oeuvresRangementRecolementDateMarquage",
      },
      {
        type: "string",
        key: "oeuvresRangementRecolementOperateurRecolement",
      },
      {
        type: "string",
        key: "oeuvresRangementRecolementDateRecolement",
      },
      {
        type: "string",
        key: "oeuvresRangementRecolementReferenceCampagneRecolement",
      },
      {
        type: "string",
        key: "oeuvresRangementRecolementAutreReferenceRecolement",
      },
      {
        type: "boolean",
        key: "oeuvresRangementRecolementRecolementOk",
      },
      {
        type: "string",
        key: "oeuvresRangementRecolementNotesRecolement",
      },
    ],
  },
  {
    type: "group",
    label: "artworksDimensions",
    showWhen: (_, includeExportOnly?: boolean) => !includeExportOnly,
    fields: [{ type: "string", key: "oeuvresDescriptionDimensionsOeuvreRaw" }],
  },
  {
    type: "group",
    label: "artworksRegie",
    fields: [
      { type: "boolean", key: "oeuvresRegieCadre" },
      { type: "string", key: "oeuvresRegieDescriptionCadre" },
      { type: "string", key: "oeuvresRegieDimensionsCadre" },
      { type: "boolean", key: "oeuvresRegieCaisseTransport" },
      { type: "string", key: "oeuvresRegieDescriptionCaisse" },
      { type: "string", key: "oeuvresRegieDimensionsCaisse" },
      { type: "string", key: "oeuvresRegiePoidsCaisse" },
      { type: "string", key: "oeuvresRegiePoidsCaisseAvecOeuvre" },
      { type: "string", key: "oeuvresRegieNotesRegie" },
      { type: "string", key: "oeuvresRegieNotesCondition" },
      { type: "boolean", key: "oeuvresRestored" },
      { type: "boolean", key: "oeuvresToRestore" },
    ],
  },
  {
    type: "group",
    label: "artworksPhotosCrédits",
    fields: [
      { type: "string", key: "oeuvresPhotosNotesPhotos" },
      { type: "string", key: "oeuvresPhotosNotesPreparationPhotos" },
      { type: "string", key: "oeuvresCreditsCreditOeuvre" },
      { type: "string", key: "oeuvresCreditsCreditPhotoOeuvre" },
      { type: "string", key: "oeuvresCreditsNatureDroits" },
      { type: "string", key: "oeuvresCreditsMontantDroits" },
      { type: "string", key: "oeuvresCreditsRemarqueCreditsDroits" },
    ],
  },
  {
    type: "group",
    label: "artworksValidationsDocumentsAdministratifs",
    fields: [
      {
        type: "string",
        key: "oeuvresDocumentsAdministratifsNotesDocumentsAdministratifs",
      },
      { type: "boolean", key: "oeuvresDocumentsAdministratifsDocumentEdite" },
      {
        type: "string",
        key: "oeuvresDocumentsAdministratifsLieuEtDateDocument",
      },
      { type: "boolean", key: "oeuvresAdminDocLettreInclusionEdit" },
      { type: "boolean", key: "oeuvresAdminDocCertificatExist" },
      { type: "string", key: "oeuvresAdministratifNotes" },
    ],
  },
  {
    type: "group",
    label: "artworksLocalisationHistorique",
    fields: [
      {
        type: "string",
        key: "oeuvresLocalisationHistoriqueCollectionCatalogue",
      },
      {
        type: "string",
        key: "oeuvresLocalisationHistoriqueCollectionNote",
      },
      {
        type: "boolean",
        key: "oeuvresLocalisationHistoriqueOeuvreNonLocalisee",
      },
      {
        type: "string",
        key: "artworkHistory.some.oeuvresLocalisationHistoriqueHistoriqueNom",
      },
      {
        type: "string",
        key: "artworkHistory.some.oeuvresLocalisationHistoriqueHistoriqueVille",
      },
      {
        type: "string",
        key: "artworkHistory.some.oeuvresLocalisationHistoriqueHistoriqueDates",
      },
      {
        type: "boolean",
        key: "artworkHistory.some.oeuvresLocalisationHistoriqueHistoriqueCollectionParticuliere",
      },
      {
        type: "string",
        key: "oeuvresLocalisationHistoriqueNotes",
      },
      {
        type: "boolean",
        key: "oeuvresLocalisationHistoriqueDepotAffectionPret",
      },
      {
        type: "string",
        key: "oeuvresLocalisationHistoriqueDateDepotAffectionPret",
      },
      {
        type: "string",
        key: "oeuvresLocalisationHistoriqueDateRetourDepotAffectionPret",
      },
      {
        type: "string",
        key: "oeuvresLocalisationHistoriqueNoteDepotAffectionPret",
      },
      {
        type: "enum",
        label: "oeuvresLocalisationHistoriqueCollection",
        key: "oeuvresLocalisationHistoriqueCollection.collectionneursDescriptionNom",
        optionsFrom: "collectors",
      },
    ],
  },
  {
    type: "group",
    label: "tiragesLabelExport",
    showWhen: (instanceData?: Instance) =>
      instanceData?.isModulePhotoEnabled ?? false,
    fields: [
      { type: "multilingual", key: "oeuvresTirageKnownDescription" },
      { type: "multilingual", key: "oeuvresTirageMasterDescription" },
      {
        type: "string",
        key: "Tirage.some.tirageDescriptionNumeroEnregistrement",
        label: "tiragesDescription.advancedSearch.numeroEnregistrement",
      },
      {
        type: "string",
        key: "Tirage.some.tirageDescriptionNumeroInventaire",
        label: "tiragesDescription.advancedSearch.numeroInventaire",
      },
      {
        type: "string",
        key: "Tirage.some.tirageDescriptionDateCreation",
        label: "tiragesDescription.advancedSearch.dateCreation",
      },
      {
        type: "dimensions",
        key: "Tirage.some.tirageDescriptionDimensionsWithMargins",
        exportOnly: true,
        label:
          "tiragesDescription.advancedSearch.tirageDescriptionDimensionsWithMargins",
      },
      {
        type: "dimensions",
        key: "Tirage.some.tirageDescriptionDimensionsWithoutMargins",
        exportOnly: true,
        label:
          "tiragesDescription.advancedSearch.tirageDescriptionDimensionsWithoutMargins",
      },
      {
        type: "string",
        key: "Tirage.some.tirageDescriptionNotes",
        label: "tiragesDescription.advancedSearch.notes",
      },
      {
        type: "string",
        key: "Tirage.some.tirageDescriptionTechnicalNotes",
        label: "tiragesDescription.advancedSearch.technicalNotes",
      },
      {
        type: "string",
        key: "Tirage.some.tirageDescriptionConditionNotes",
        label: "tiragesDescription.advancedSearch.conditionNotes",
      },
      {
        type: "string",
        key: "Tirage.some.tiragePublicationStatus",
        label: "tiragesDescription.advancedSearch.publicationStatus",
      },
      {
        type: "multilingual",
        key: "Tirage.some.tirageDescriptionType",
        label: "tiragesDescription.advancedSearch.type",
      },
      {
        type: "multilingual",
        key: "Tirage.some.tirageDescriptionPrinter",
        label: "tiragesDescription.advancedSearch.printer",
      },
      {
        type: "multilingual",
        key: "Tirage.some.tirageDescriptionTechnique",
        label: "tiragesDescription.advancedSearch.technique",
      },
      {
        type: "multilingual",
        key: "Tirage.some.tirageDescriptionSupport",
        label: "tiragesDescription.advancedSearch.support",
      },
      {
        type: "multilingual",
        key: "Tirage.some.tirageDescriptionEditionNumber",
        label: "tiragesDescription.advancedSearch.editionNumber",
      },
      {
        type: "multilingual",
        key: "Tirage.some.tirageDescriptionSignatureInscription",
        label: "tiragesDescription.advancedSearch.signatureInscription",
      },
      {
        type: "multilingual",
        key: "Tirage.some.tirageDescriptionSignatureInscriptionDos",
        label: "tiragesDescription.advancedSearch.signatureInscriptionDos",
      },
      {
        type: "string",
        key: "Tirage.some.tirageEstimationDescription",
        label: "tiragesDescription.advancedSearch.estimationDescription",
      },
      {
        type: "currency",
        key: "Tirage.some.tirageEstimationAmount",
        label: "tiragesDescription.advancedSearch.estimationAmount",
      },
      {
        type: "string",
        key: "Tirage.some.tirageSaleDescription",
        label: "tiragesDescription.advancedSearch.saleDescription",
      },
      {
        type: "currency",
        key: "Tirage.some.tirageSaleAmount",
        label: "tiragesDescription.advancedSearch.saleAmount",
      },
      {
        type: "string",
        key: "Tirage.some.tirageAcquisitionDescription",
        label: "tiragesDescription.advancedSearch.acquisitionDescription",
      },
      {
        type: "currency",
        key: "Tirage.some.tirageAcquisitionAmount",
        label: "tiragesDescription.advancedSearch.acquisitionAmount",
      },
      {
        type: "string",
        key: "Tirage.some.tirageProductionDescription",
        label: "tiragesDescription.advancedSearch.productionDescription",
      },
      {
        type: "currency",
        key: "Tirage.some.tirageProductionAmount",
        label: "tiragesDescription.advancedSearch.productionAmount",
      },
      {
        type: "string",
        key: "Tirage.some.tirageEncadrementDescription",
        label: "tiragesDescription.advancedSearch.encadrementDescription",
      },
      {
        type: "currency",
        key: "Tirage.some.tirageEncadrementAmount",
        label: "tiragesDescription.advancedSearch.encadrementAmount",
      },
      {
        type: "string",
        key: "Tirage.some.tirageAssuranceDescription",
        label: "tiragesDescription.advancedSearch.assuranceDescription",
      },
      {
        type: "currency",
        key: "Tirage.some.tirageAssuranceAmount",
        label: "tiragesDescription.advancedSearch.assuranceAmount",
      },
      {
        type: "string",
        key: "Tirage.some.tirageStorageLocation1",
        label: "tiragesDescription.advancedSearch.storageLocation1",
      },
      {
        type: "string",
        key: "Tirage.some.tirageStorageLocation2",
        label: "tiragesDescription.advancedSearch.storageLocation2",
      },
      {
        type: "string",
        key: "Tirage.some.tirageStorageLocation3",
        label: "tiragesDescription.advancedSearch.storageLocation3",
      },
      {
        type: "boolean",
        key: "Tirage.some.tirageOeuvreNonLocalisee",
        label: "tiragesDescription.advancedSearch.oeuvreNonLocalisee",
      },
    ],
  },
  {
    type: "group",
    label: "artworksSculptureLabelExport",
    showWhen: (instanceData?: Instance) =>
      instanceData?.isModuleSculptureEnabled ?? false,
    fields: [
      {
        type: "multilingual",
        key: "oeuvresSculptureDescriptionEditions",
      },
      {
        type: "boolean",
        key: "oeuvresSculptureDescriptionEditionsPublished",
      },
      {
        type: "multilingual",
        key: "oeuvresSculpturePresentationSculpture",
      },
      {
        type: "boolean",
        key: "oeuvresSculpturePresentationSculpturePublished",
      },
      {
        type: "multilingual",
        key: "oeuvresSculptureNotesGenerales",
      },
      {
        type: "boolean",
        key: "oeuvresSculptureNotesGeneralesPublished",
      },
      {
        type: "multilingual",
        key: "oeuvresSculptureMouleDescription",
      },
      {
        type: "string",
        key: "oeuvresSculptureMouleNombreElements",
      },
      {
        type: "dimensions",
        key: "oeuvresSculptureMouleDimensions",
        exportOnly: true,
      },
      {
        type: "string",
        key: "oeuvresSculptureMouleDimensionsPersonnalisees",
      },
      {
        type: "string",
        key: "oeuvresSculptureMoulePoids",
      },
      {
        type: "string",
        key: "oeuvresSculptureMouleLocalisation",
      },
      {
        type: "boolean",
        key: "oeuvresSculptureMouleNonLocalise",
      },
      {
        type: "boolean",
        key: "oeuvresSculptureMouleDestroy",
      },
      {
        type: "string",
        key: "oeuvresSculptureMouleFabricant",
      },
      {
        type: "currency",
        key: "oeuvresSculptureMouleCoutRevient",
      },
      {
        type: "string",
        key: "oeuvresSculptureMouleNotes",
      },
    ],
  },
  {
    type: "group",
    label: "sculptureEditionsLabelExport",
    showWhen: (instanceData?: Instance) =>
      instanceData?.isModuleSculptureEnabled ?? false,
    fields: [
      {
        type: "string",
        key: "masterForSculptureEditions.some.sculptureEditionNumeroEnregistrement",
        label: "sculptureEditions.advancedSearch.numeroEnregistrement",
      },
      {
        type: "string",
        key: "masterForSculptureEditions.some.sculptureEditionNumeroInventaire",
        label: "sculptureEditions.advancedSearch.numeroInventaire",
      },
      {
        type: "string",
        key: "masterForSculptureEditions.some.sculptureEditionAutreNumero",
        label: "sculptureEditions.advancedSearch.autreNumero",
      },
      {
        type: "string",
        key: "masterForSculptureEditions.some.sculptureEditionAncienNumero",
        label: "sculptureEditions.advancedSearch.ancienNumero",
      },
      {
        type: "string",
        key: "masterForSculptureEditions.some.sculptureEditionPublicationStatus",
        label: "sculptureEditions.advancedSearch.publicationStatus",
      },
      {
        type: "string",
        key: "masterForSculptureEditions.some.sculptureEditionTypeEdition",
        label: "sculptureEditions.advancedSearch.typeEdition",
      },
      {
        type: "string",
        key: "masterForSculptureEditions.some.sculptureEditionDescriptionSocle",
        label: "sculptureEditions.advancedSearch.descriptionSocle",
      },
      {
        type: "string",
        key: "masterForSculptureEditions.some.sculptureEditionDateRealisation",
        label: "sculptureEditions.advancedSearch.dateRealisation",
      },
      {
        type: "string",
        key: "masterForSculptureEditions.some.sculptureEditionFondeurFabricant",
        label: "sculptureEditions.advancedSearch.fondeurFabricant",
      },
      {
        type: "string",
        key: "masterForSculptureEditions.some.sculptureEditionNumeroFonte",
        label: "sculptureEditions.advancedSearch.numeroFonte",
      },
      {
        type: "multilingual",
        key: "masterForSculptureEditions.some.sculptureEditionTechnique",
        label: "sculptureEditions.advancedSearch.technique",
      },
      {
        type: "multilingual",
        key: "masterForSculptureEditions.some.sculptureEditionMateriau",
        label: "sculptureEditions.advancedSearch.materiau",
      },
      {
        type: "multilingual",
        key: "masterForSculptureEditions.some.sculptureEditionSupport",
        label: "sculptureEditions.advancedSearch.support",
      },
      {
        type: "multilingual",
        key: "masterForSculptureEditions.some.sculptureEditionNumerotation",
        label: "sculptureEditions.advancedSearch.numerotation",
      },
      {
        type: "multilingual",
        key: "masterForSculptureEditions.some.sculptureEditionSignature",
        label: "sculptureEditions.advancedSearch.signature",
      },
      {
        type: "multilingual",
        key: "masterForSculptureEditions.some.sculptureEditionMarqueTampons",
        label: "sculptureEditions.advancedSearch.marqueTampons",
      },
      {
        type: "boolean",
        key: "masterForSculptureEditions.some.sculptureEditionMarqueTamponsPublished",
        label: "sculptureEditions.advancedSearch.marqueTamponsPublished",
      },
      {
        type: "string",
        key: "masterForSculptureEditions.some.sculptureEditionEstimationDescription",
        label: "sculptureEditions.advancedSearch.estimationDescription",
      },
      {
        type: "currency",
        key: "masterForSculptureEditions.some.sculptureEditionEstimationMontant",
        label: "sculptureEditions.advancedSearch.estimationMontant",
      },
      {
        type: "string",
        key: "masterForSculptureEditions.some.sculptureEditionAssuranceDescription",
        label: "sculptureEditions.advancedSearch.assuranceDescription",
      },
      {
        type: "currency",
        key: "masterForSculptureEditions.some.sculptureEditionAssuranceMontant",
        label: "sculptureEditions.advancedSearch.assuranceMontant",
      },
      {
        type: "string",
        key: "masterForSculptureEditions.some.sculptureEditionVenteDescription",
        label: "sculptureEditions.advancedSearch.venteDescription",
      },
      {
        type: "currency",
        key: "masterForSculptureEditions.some.sculptureEditionVenteMontant",
        label: "sculptureEditions.advancedSearch.venteMontant",
      },
      {
        type: "string",
        key: "masterForSculptureEditions.some.sculptureEditionAcquisitionDescription",
        label: "sculptureEditions.advancedSearch.acquisitionDescription",
      },
      {
        type: "currency",
        key: "masterForSculptureEditions.some.sculptureEditionAcquisitionMontant",
        label: "sculptureEditions.advancedSearch.acquisitionMontant",
      },
      {
        type: "dimensions",
        key: "masterForSculptureEditions.some.sculptureEditionDimensions",
        label: "sculptureEditions.advancedSearch.dimensions",
        exportOnly: true,
      },
      {
        type: "dimensions",
        key: "masterForSculptureEditions.some.sculptureEditionDimensionsSocle",
        label: "sculptureEditions.advancedSearch.dimensionsSocle",
        exportOnly: true,
      },
      {
        type: "dimensions",
        key: "masterForSculptureEditions.some.sculptureEditionDimensionsBrutes",
        label: "sculptureEditions.advancedSearch.dimensionsBrutes",
        exportOnly: true,
      },
      {
        type: "dimensions",
        key: "masterForSculptureEditions.some.sculptureEditionDimensionsAvecSocle",
        label: "sculptureEditions.advancedSearch.dimensionsAvecSocle",
        exportOnly: true,
      },
      {
        type: "string",
        key: "masterForSculptureEditions.some.sculptureEditionPoids",
        label: "sculptureEditions.advancedSearch.poids",
      },
      {
        type: "string",
        key: "masterForSculptureEditions.some.sculptureEditionPoidsSocle",
        label: "sculptureEditions.advancedSearch.poidsSocle",
      },
      {
        type: "string",
        key: "masterForSculptureEditions.some.sculptureEditionPoidsBrutes",
        label: "sculptureEditions.advancedSearch.poidsBrutes",
      },
      {
        type: "string",
        key: "masterForSculptureEditions.some.sculptureEditionPoidsSoclePiedouche",
        label: "sculptureEditions.advancedSearch.poidsSoclePiedouche",
      },
      {
        type: "string",
        key: "masterForSculptureEditions.some.sculptureEditionDimensionsPersonnalisees",
        label: "sculptureEditions.advancedSearch.dimensionsPersonnalisees",
      },
      {
        type: "string",
        key: "masterForSculptureEditions.some.sculptureEditionPoidsLibre",
        label: "sculptureEditions.advancedSearch.poidsLibre",
      },
      {
        type: "string",
        key: "masterForSculptureEditions.some.sculptureEditionNotesPoids",
        label: "sculptureEditions.advancedSearch.notesPoids",
      },
      {
        type: "string",
        key: "masterForSculptureEditions.some.sculptureEditionLocation1",
        label: "sculptureEditions.advancedSearch.location1",
      },
      {
        type: "string",
        key: "masterForSculptureEditions.some.sculptureEditionLocation2",
        label: "sculptureEditions.advancedSearch.location2",
      },
      {
        type: "string",
        key: "masterForSculptureEditions.some.sculptureEditionLocation3",
        label: "sculptureEditions.advancedSearch.location3",
      },
      {
        type: "string",
        key: "masterForSculptureEditions.some.sculptureEditionNotes",
        label: "sculptureEditions.advancedSearch.notes",
      },
      {
        type: "string",
        key: "masterForSculptureEditions.some.sculptureEditionNotesTechniques",
        label: "sculptureEditions.advancedSearch.notesTechniques",
      },
      ...withMasterArtworkSculptureEditionSearchKeys(
        sculptureEditionProvenanceAdvancedSearchFields,
      ),
    ],
  },
  {
    type: "group",
    label: "sources",
    fields: [{ type: "string", key: "sources" }],
  },
  {
    type: "group",
    label: "notes",
    fields: [
      { type: "string", key: "oeuvresValidationNotesDeTravail" },
      { type: "string", key: "oeuvresValidationRemarquesPersonnelles" },
      {
        type: "multilingual",
        key: "oeuvresValidationRemarquesPubliquesMultilingual",
      },
    ],
  },
  {
    type: "group",
    label: "artworksSuiviFinancier",
    fields: [
      {
        type: "string",
        key: "MonetaryTransaction.some.title",
        label: "suiviFinancier.estimate.title",
        additionalQuery: [{ category: { equals: "estimate" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.date",
        label: "suiviFinancier.estimate.date",
        additionalQuery: [{ category: { equals: "estimate" } }],
      },
      {
        type: "number",
        multiplyBy: 100,
        key: "MonetaryTransaction.some.amount",
        label: "suiviFinancier.estimate.amount",
        additionalQuery: [{ category: { equals: "estimate" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.currency",
        label: "suiviFinancier.estimate.currency",
        additionalQuery: [{ category: { equals: "estimate" } }],
      },
      {
        type: "string",
        label: "suiviFinancier.disposition.title",
        key: "oeuvreVenteTitre",
      },
      {
        type: "string",
        label: "suiviFinancier.disposition.date",
        key: "oeuvreVenteDate",
      },
      {
        type: "currency",
        label: "suiviFinancier.disposition.amount",
        key: "oeuvreVenteMontant",
      },
      {
        type: "currency",
        label: "suiviFinancier.disposition.amountTVA",
        key: "oeuvreVenteMontantTVA",
      },
      {
        type: "currency",
        label: "suiviFinancier.disposition.amountTOP",
        key: "oeuvreVenteMontantTOP",
      },
      {
        type: "string",
        label: "suiviFinancier.acquisition.title",
        key: "oeuvreAchatTitre",
      },
      {
        type: "string",
        label: "suiviFinancier.acquisition.date",
        key: "oeuvreAchatDate",
      },
      {
        type: "currency",
        label: "suiviFinancier.acquisition.amount",
        key: "oeuvreAchatMontant",
      },
      {
        type: "currency",
        label: "suiviFinancier.acquisition.amountTVA",
        key: "oeuvreAchatMontantTVA",
      },
      {
        type: "currency",
        label: "suiviFinancier.acquisition.amountDroitsDeDouane",
        key: "oeuvreAchatMontantDroitsDeDouane",
      },
      {
        type: "currency",
        label: "suiviFinancier.acquisition.amountDroitsDeSuite",
        key: "oeuvreAchatMontantDroitsDeSuite",
      },
      {
        type: "currency",
        label: "suiviFinancier.acquisition.amountFraisDeVente",
        key: "oeuvreAchatMontantFraisDeVente",
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.title",
        label: "suiviFinancier.production.title",
        additionalQuery: [{ category: { equals: "production" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.date",
        label: "suiviFinancier.production.date",
        additionalQuery: [{ category: { equals: "production" } }],
      },
      {
        type: "number",
        multiplyBy: 100,
        key: "MonetaryTransaction.some.amount",
        label: "suiviFinancier.production.amount",
        additionalQuery: [{ category: { equals: "production" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.currency",
        label: "suiviFinancier.production.currency",
        additionalQuery: [{ category: { equals: "production" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.title",
        label: "suiviFinancier.transport.title",
        additionalQuery: [{ category: { equals: "transport" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.date",
        label: "suiviFinancier.transport.date",
        additionalQuery: [{ category: { equals: "transport" } }],
      },
      {
        type: "number",
        multiplyBy: 100,
        key: "MonetaryTransaction.some.amount",
        label: "suiviFinancier.transport.amount",
        additionalQuery: [{ category: { equals: "transport" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.currency",
        label: "suiviFinancier.transport.currency",
        additionalQuery: [{ category: { equals: "transport" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.title",
        label: "suiviFinancier.restoration.title",
        additionalQuery: [{ category: { equals: "restoration" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.date",
        label: "suiviFinancier.restoration.date",
        additionalQuery: [{ category: { equals: "restoration" } }],
      },
      {
        type: "number",
        multiplyBy: 100,
        key: "MonetaryTransaction.some.amount",
        label: "suiviFinancier.restoration.amount",
        additionalQuery: [{ category: { equals: "restoration" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.currency",
        label: "suiviFinancier.restoration.currency",
        additionalQuery: [{ category: { equals: "restoration" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.title",
        label: "suiviFinancier.framing.title",
        additionalQuery: [{ category: { equals: "framing" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.date",
        label: "suiviFinancier.framing.date",
        additionalQuery: [{ category: { equals: "framing" } }],
      },
      {
        type: "number",
        multiplyBy: 100,
        key: "MonetaryTransaction.some.amount",
        label: "suiviFinancier.framing.amount",
        additionalQuery: [{ category: { equals: "framing" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.currency",
        label: "suiviFinancier.framing.currency",
        additionalQuery: [{ category: { equals: "framing" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.title",
        label: "suiviFinancier.photography.title",
        additionalQuery: [{ category: { equals: "photography" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.date",
        label: "suiviFinancier.photography.date",
        additionalQuery: [{ category: { equals: "photography" } }],
      },
      {
        type: "number",
        multiplyBy: 100,
        key: "MonetaryTransaction.some.amount",
        label: "suiviFinancier.photography.amount",
        additionalQuery: [{ category: { equals: "photography" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.currency",
        label: "suiviFinancier.photography.currency",
        additionalQuery: [{ category: { equals: "photography" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.title",
        label: "suiviFinancier.expertise.title",
        additionalQuery: [{ category: { equals: "expertise" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.date",
        label: "suiviFinancier.expertise.date",
        additionalQuery: [{ category: { equals: "expertise" } }],
      },
      {
        type: "number",
        multiplyBy: 100,
        key: "MonetaryTransaction.some.amount",
        label: "suiviFinancier.expertise.amount",
        additionalQuery: [{ category: { equals: "expertise" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.currency",
        label: "suiviFinancier.expertise.currency",
        additionalQuery: [{ category: { equals: "expertise" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.title",
        label: "suiviFinancier.insurance.title",
        additionalQuery: [{ category: { equals: "insurance" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.date",
        label: "suiviFinancier.insurance.date",
        additionalQuery: [{ category: { equals: "insurance" } }],
      },
      {
        type: "number",
        multiplyBy: 100,
        key: "MonetaryTransaction.some.amount",
        label: "suiviFinancier.insurance.amount",
        additionalQuery: [{ category: { equals: "insurance" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.currency",
        label: "suiviFinancier.insurance.currency",
        additionalQuery: [{ category: { equals: "insurance" } }],
      },
      { type: "string", key: "oeuvreSuiviFinancierNotes" },
    ],
  },
  attachmentPhotoAdvancedSearchGroup,
];

/**
 * Returns the artwork advanced search config, filtered based on instance settings.
 * Filters out groups and fields that have a `showWhen` condition that evaluates to false.
 * @param instanceData - Instance data for conditional filtering
 * @param includeExportOnly - If true, includes fields/groups marked as exportOnly (default: false)
 */
export const getArtworkAdvancedSearchConfig = (
  instanceData?: Instance,
  includeExportOnly: boolean = false,
): AdvancedSearchConfig => {
  return artworkAdvancedSearchConfig
    .map((item) => {
      // Filter out export-only items if not including them
      if (!includeExportOnly && item.exportOnly) {
        return null;
      }

      // Check if the item has a showWhen condition
      if (item.showWhen && !item.showWhen(instanceData, includeExportOnly)) {
        return null; // Filter out this item
      }

      // If it's a group, also filter its fields
      if (item.type === "group") {
        const filteredFields = item.fields.filter((field) => {
          // Filter out export-only fields if not including them
          if (!includeExportOnly && field.exportOnly) {
            return false;
          }
          // Include field if it doesn't have showWhen or if showWhen returns true
          return !field.showWhen || field.showWhen(instanceData, true);
        });

        // Only return the group if it has fields after filtering
        if (filteredFields.length === 0) {
          return null;
        }

        return {
          ...item,
          fields: filteredFields,
        };
      }

      return item;
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);
};
